import { svgSend } from '@shared/assets';
import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { useFetchUsers } from '@shared/graphql/hooks/users/useGetUsers';
import useOptionalRef from '@shared/hooks/use-optional-ref';
import { usePlatform } from '@shared/hooks/usePlatform';
import { User } from '@shared/types/user';
import { makeElementClassNameFactory, makeRootClassName } from '@shared/utils';
import clsx from 'clsx';
import { ReactNode, Ref, useId, useRef, useState } from 'react';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import { Button } from '../button';
import { CircularProgress } from '../circular-progress';
import { useEmojis } from './useEmojis';

const ROOT = makeRootClassName('MentionInput');
const el = makeElementClassNameFactory(ROOT);

const getNameField = (user: User) => {
  if (user.name && user.name.length > 0) return user.name;
  if (user.userName && user.userName.length > 0) return user.userName;
};

const stringifyUser = (user: User) => {
  const fields = [];
  const nameField = getNameField(user);
  if (nameField && nameField.length > 0) fields.push(nameField);
  if (user.email && user.email.length > 0) fields.push(user.email);
  return fields.join(' - ');
};

const getBestDisplayName = (user: User) => {
  if (user.name && user.name.length > 0) return user.name;
  if (user.userName && user.userName.length > 0) return user.userName;
  if (user.email && user.email.length > 0) return user.email;
  return 'Unnamed User';
};

const toMention = (user: User) => ({
  id: user.id,
  display: getBestDisplayName(user),
});

type SubmitProps = {
  canSubmitWithoutText?: boolean;
  loading: boolean;
  isDisabled: boolean;
  onSubmit: () => void;
};

type MentionInputProps = {
  value: string;
  onChange: (val: string) => void;
  submit: SubmitProps;
  inputRef?: Ref<HTMLTextAreaElement>;
  /** Optional content rendered inside the input box, below the text area. */
  footer?: ReactNode;
  placeholder?: string;
  tabCompletion?: string;
  textSize?: MentionInputTextSize;
};

export type MentionInputTextSize = 'sm' | 'md';

const DEFAULT_PLACEHOLDER = 'Start typing, using "@" to mention someone...';
const FONT_SIZE_BY_TEXT_SIZE: Record<MentionInputTextSize, number> = {
  sm: 12,
  md: 14,
};
const MIN_HEIGHT_BY_TEXT_SIZE: Record<MentionInputTextSize, number> = {
  sm: 63,
  md: 54,
};

export function MentionInput({
  value,
  onChange,
  submit,
  inputRef: inputRefProp,
  footer,
  placeholder = DEFAULT_PLACEHOLDER,
  tabCompletion,
  textSize = 'sm',
}: MentionInputProps) {
  const inputRef = useOptionalRef(inputRefProp);
  const { me } = useMeContext();
  const [users, setUsers] = useState<User[]>([]);
  const { fetchEmojis, getEmojiById } = useEmojis();
  const tabHintId = useId();
  const acceptedCompletionRef = useRef<string | null>(null);

  const { fetchUsers } = useFetchUsers({
    organizationIds: me?.organizationsDetails.map((org) => org.id) ?? [],
  });

  const replaceEmojis = (text: string) => {
    const emojiRegex = /\\emoji\{([^}]+)\}\\/g;
    const out = text.replace(emojiRegex, (match, id) => {
      return getEmojiById(id)?.emoji ?? match;
    });
    return out;
  };

  const setAndReturnUsers = async (newUsers: User[]) => {
    setUsers(newUsers);
    return newUsers;
  };

  const fetchMentionOptions = async (
    query: string,
    callback: (options: SuggestionDataItem[]) => void
  ) => {
    const MAX_RESULTS = 10;
    await fetchUsers(query)
      .then(setAndReturnUsers)
      .then((res) => res.map(toMention).slice(0, MAX_RESULTS))
      .then(callback);
  };

  const isDisabled = submit.loading;
  const offeredCompletion = value || isDisabled ? undefined : tabCompletion;

  const onKeyDown = (e: React.KeyboardEvent) => {
    const isForwardTab = e.key === 'Tab' && !e.shiftKey;
    if (isForwardTab && offeredCompletion) {
      e.preventDefault();
      acceptedCompletionRef.current = offeredCompletion;
      onChange(offeredCompletion);
      return;
    }
    // NOTE(wassim): a repeated Tab would otherwise focus Attach, so the next
    // Enter opens the file picker instead of sending the drafted suggestion.
    if (isForwardTab && value === acceptedCompletionRef.current) {
      e.preventDefault();
      return;
    }
    const shiftIsPressed = e.shiftKey;
    const isEnter = e.key === 'Enter';
    const hasSubmittableContent =
      Boolean(value) || submit.canSubmitWithoutText === true;
    if (
      isEnter &&
      !shiftIsPressed &&
      !submit.loading &&
      !submit.isDisabled &&
      hasSubmittableContent
    ) {
      submit.onSubmit();
    }
  };

  const UserSuggestion = (
    suggestion: SuggestionDataItem,
    search: string,
    highlightedDisplay: React.ReactNode
  ) => {
    const user = users.find((u) => u.id === suggestion.id);
    if (user) {
      const userString = stringifyUser(user);
      return <HighlightMatchingSubStrings text={userString} search={search} />;
    }
    return highlightedDisplay;
  };

  const EmojiSuggestion = (suggestion: SuggestionDataItem, search: string) => {
    const e = getEmojiById(`${suggestion.id}`);
    if (!e) return null;
    const { id, emoji } = e;
    return (
      <div className="flex flex-row gap-3">
        <span>{emoji}</span>
        <HighlightMatchingSubStrings text={id} search={search} />
      </div>
    );
  };

  const findEmoji = (id: string) => getEmojiById(id)?.emoji ?? '?';

  const submitFromSendButton = () => {
    submit.onSubmit();
    inputRef.current?.focus();
  };

  const hasSubmittableContent =
    Boolean(value.trim()) || submit.canSubmitWithoutText === true;
  const isSendButtonVisible = hasSubmittableContent || submit.loading;

  return (
    <div className={clsx(ROOT, { 'is-disabled': isDisabled })}>
      <MentionsInput
        style={getStyles(
          FONT_SIZE_BY_TEXT_SIZE[textSize],
          MIN_HEIGHT_BY_TEXT_SIZE[textSize]
        )}
        inputRef={inputRef}
        value={value}
        onChange={(e) => {
          acceptedCompletionRef.current = null;
          onChange(replaceEmojis(e.target.value));
        }}
        onKeyDown={onKeyDown}
        aria-describedby={offeredCompletion ? tabHintId : undefined}
        placeholder={offeredCompletion ?? placeholder}
        customSuggestionsContainer={(children) => (
          <div className={el`suggestions-container`}>{children}</div>
        )}
        forceSuggestionsAboveCursor
        allowSpaceInQuery
        // NOTE(mike): readOnly rather than disabled, because browsers blur
        // disabled inputs and don't restore focus once they're re-enabled.
        readOnly={isDisabled}
      >
        <Mention
          style={mentionStyles}
          trigger="@"
          data={fetchMentionOptions}
          markup={'<@__id__:user:__display__>'}
          displayTransform={(_, display) => `@${display}`}
          renderSuggestion={UserSuggestion}
        />
        <Mention
          trigger=":"
          markup="\emoji{__id__}\"
          data={fetchEmojis}
          displayTransform={(id, _) => findEmoji(id)}
          renderSuggestion={EmojiSuggestion}
        />
      </MentionsInput>
      {footer && <div className="flex items-center pr-10 pt-1">{footer}</div>}
      {offeredCompletion && (
        <div
          className={clsx(
            el`tab-hint`,
            isSendButtonVisible && el`tab-hint-beside-send`
          )}
        >
          <kbd aria-hidden>Tab</kbd>
          <span className="sr-only" id={tabHintId}>
            Press Tab to draft this suggestion. Shift+Tab moves focus back.
          </span>
        </div>
      )}
      {isSendButtonVisible && (
        <SendButton
          hasFooter={Boolean(footer)}
          isDisabled={submit.isDisabled}
          loading={submit.loading}
          onPress={submitFromSendButton}
        />
      )}
    </div>
  );
}

type SendButtonProps = {
  hasFooter: boolean;
  isDisabled: boolean;
  loading: boolean;
  onPress: () => void;
};
function SendButton(p: SendButtonProps) {
  const { isWeb } = usePlatform();

  return (
    <div
      className={clsx(
        el`send-button`,
        isWeb && !p.hasFooter && el`send-button-web`
      )}
    >
      {p.loading ? (
        <CircularProgress size="xs" />
      ) : (
        <Button
          icon={svgSend}
          onPress={p.onPress}
          isDisabled={p.isDisabled}
          size="xs"
          variant="vibrant"
        />
      )}
    </div>
  );
}

type HighlightMatchingSubStringsProps = {
  text: string;
  search: string;
};
function HighlightMatchingSubStrings({
  text,
  search,
}: HighlightMatchingSubStringsProps) {
  if (!search) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${search})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) => {
        if (part.toLowerCase() === search.toLowerCase()) {
          return (
            <span key={i}>
              <strong>{part}</strong>
            </span>
          );
        }
        return part;
      })}
    </span>
  );
}

// TODO(mike) Figure out how to style in a way that is more consistent with the rest of the app
const getStyles = (fontSize: number, minHeight: number) => ({
  control: {
    fontSize,
    fontWeight: 'normal',
    color: 'black',
    backgroundColor: 'transparent',
  },
  input: {
    outline: 'none',
    padding: '5px',
  },
  highlighter: {
    border: 0,
    padding: '5px',
  },

  '&multiLine': {
    control: {
      minHeight,
    },
  },

  suggestions: {
    list: {
      backgroundColor: 'white',
      border: '0px solid rgba(0,0,0,0.15)',
      fontSize: 12,
    },
    item: {
      padding: '5px 15px',
      '&focused': {
        backgroundColor: '#eeeeee',
      },
      borderRadius: 3,
    },
  },
});

// NOTE(mike): The styling here is a bit limited because react-mentions styles
// by overlaying the rendered mention over the text input. I don't recommend
// trying to introduce any spacing-related styles (padding, margin, etc) here.
const mentionStyles = {
  backgroundColor: '#e5eaed',
  borderRadius: 3,
  outline: '2px solid #e5eaed',
  outlineOffset: '-1px',
};
